// Skio Rules API — live volume-discount tiers.
// https://integrate.skio.com/skio/rules/rules-api
//
// Semantics, confirmed against this store's payload and Skio's own
// apply-volume-discounts reference script:
//
//   * Only ONE rule applies: the eligible rule with the LARGEST
//     minQuantityToDiscount. Tiers do not stack. A 4-bag order takes the
//     4-bag rule alone, not the 2-bag plus 3-bag plus 4-bag rules.
//   * FIXED_VALUE / FIXED_AMOUNT discountAmount is DOLLARS OFF THE LINE
//     TOTAL, not off each unit. Verified arithmetically against the live rules
//     and both reviewed first-shipment and renewal ladders.
//   * maxQuantityToDiscount is nullable; null means the tier is unbounded.
//
// This API drives storefront DISPLAY pricing. Skio/Shopify automatic discounts
// apply the corresponding cart adjustment separately; this runtime does not
// fetch or apply discount codes. Commerce QA must verify the cart's applied
// discount label and exact charge against the displayed tier before release.
const SKIO_RULES_ENDPOINT = 'https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname';
const SKIO_FETCH_TIMEOUT_MS = 4000;

/**
 * Shared across every buy box on the page. Several sections
 * (winks-buybox-segmented, winks-epdp-buybox, winks-article-offer) mount this
 * same component, and a landing page can carry more than one, so the request
 * is deduped at module scope rather than per instance.
 * @type {Promise<SkioRule[]> | undefined}
 */
let skioRulesPromise;

/**
 * @typedef {object} SkioRule
 * @property {number} min
 * @property {number} max - Infinity when the tier is unbounded.
 * @property {number} amount
 * @property {string} type
 * @property {Set<string>} variantIds
 */

/**
 * Drops anything malformed rather than trusting the payload: a rule missing a
 * quantity, an amount, or a variant target cannot price anything, and a bad
 * entry must not take the whole ladder down with it.
 *
 * @param {unknown} payload
 * @returns {SkioRule[]}
 */
function normalizeSkioRules(payload) {
  const rules = Array.isArray(/** @type {any} */ (payload)?.rules) ? /** @type {any} */ (payload).rules : [];

  return rules.reduce((/** @type {SkioRule[]} */ list, rule) => {
    const min = Number(rule?.minQuantityToDiscount);
    const amount = Number(rule?.discountAmount);
    if (!Number.isFinite(min) || min < 1 || !Number.isFinite(amount) || amount <= 0) return list;

    const rawMax = rule?.maxQuantityToDiscount;
    const max = rawMax == null ? Infinity : Number(rawMax);
    if ((!Number.isFinite(max) && max !== Infinity) || max < min) return list;

    const type = String(rule?.discountType || '').toUpperCase();
    const supportedType = ['PERCENTAGE', 'PERCENT', 'FIXED_VALUE', 'FIXED_AMOUNT'].includes(type);
    if (!supportedType || ((type === 'PERCENTAGE' || type === 'PERCENT') && amount >= 100)) return list;

    // Compare the numeric id exactly. Skio's reference script uses
    // `gid.includes(item.id)`, which substring-matches: variant 1234 would
    // falsely satisfy a rule targeting 51234 and discount the wrong product.
    const variantIds = new Set();
    const targets = Array.isArray(rule?.productVariantIds) ? rule.productVariantIds : [];
    targets.forEach((gid) => {
      const id = String(gid).match(/(\d+)\s*$/)?.[1];
      if (id) variantIds.add(id);
    });
    if (!variantIds.size) return list;

    list.push({ min, max, amount, type, variantIds });
    return list;
  }, []);
}

/**
 * Any failure resolves to an empty ladder so the buy box silently keeps the
 * Liquid-rendered pricing. A discount service being unreachable must never
 * blank out a price or block add-to-cart.
 *
 * @returns {Promise<SkioRule[]>}
 */
function fetchSkioRules() {
  if (skioRulesPromise) return skioRulesPromise;

  const shop = window.Shopify?.shop;
  if (!shop) {
    skioRulesPromise = Promise.resolve([]);
    return skioRulesPromise;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SKIO_FETCH_TIMEOUT_MS);

  skioRulesPromise = fetch(`${SKIO_RULES_ENDPOINT}?domain=${encodeURIComponent(shop)}`, { signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .then(normalizeSkioRules)
    .catch(() => [])
    .finally(() => window.clearTimeout(timeoutId));

  return skioRulesPromise;
}

/**
 * The eligible rule with the largest minimum quantity wins, matching Skio's
 * evaluation order. Ties keep the first match, which is irrelevant in practice
 * because two rules with the same minimum would be a Skio misconfiguration.
 *
 * @param {SkioRule[]} rules
 * @param {string | undefined} variantId
 * @param {number} quantity
 * @returns {SkioRule | null}
 */
function resolveSkioRule(rules, variantId, quantity) {
  if (!rules.length || !variantId || !Number.isFinite(quantity)) return null;

  const id = String(variantId).trim();
  if (!id) return null;

  return rules.reduce((/** @type {SkioRule | null} */ best, rule) => {
    if (!rule.variantIds.has(id)) return best;
    if (quantity < rule.min || quantity > rule.max) return best;
    return best && best.min >= rule.min ? best : rule;
  }, null);
}

/**
 * Rejects a rule that would make the line free or negative. Returning null
 * tells the caller to retain the reviewed Liquid fallback instead.
 *
 * @param {SkioRule} rule
 * @param {number} lineTotalCents
 * @returns {number | null}
 */
function skioDiscountCents(rule, lineTotalCents) {
  if (!(lineTotalCents > 0)) return null;

  let discount = 0;
  if (rule.type === 'PERCENTAGE' || rule.type === 'PERCENT') {
    discount = (lineTotalCents * rule.amount) / 100;
  } else if (rule.type === 'FIXED_VALUE' || rule.type === 'FIXED_AMOUNT') {
    discount = rule.amount * 100;
  } else {
    return null;
  }

  discount = Math.round(discount);
  if (!Number.isFinite(discount) || discount <= 0 || discount >= lineTotalCents) return null;
  return discount;
}

/**
 * Headline money figures read as whole dollars. Rounded UP rather than to
 * nearest so a displayed price can never sit below what the cart actually
 * charges. This is what presents the accepted $171.99 quarterly charge as
 * $172 in the card headline. Exact cents remain visible in the charge summary.
 *
 * @param {number} cents
 * @returns {number} cents, as a whole-dollar multiple of 100
 */
function ceilToWholeDollars(cents) {
  return Math.ceil(cents / 100) * 100;
}

class WinksSegmentedBuybox extends HTMLElement {
  #abortController;

  /** @type {SkioRule[]} */
  #skioRules = [];

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const galleryScroll = this.querySelector('[data-winks-gallery-scroll]');
    const galleryPrevious = this.querySelector('[data-winks-gallery-previous]');
    const galleryNext = this.querySelector('[data-winks-gallery-next]');
    if (galleryScroll instanceof HTMLElement) {
      const desktopGallery = window.matchMedia('(min-width: 1200px)');
      const galleryStep = () => {
        const thumbnail = galleryScroll.querySelector('[data-winks-media-button]');
        if (!(thumbnail instanceof HTMLElement)) return galleryScroll.clientHeight;
        const gap = Number.parseFloat(getComputedStyle(galleryScroll).rowGap) || 0;
        return thumbnail.getBoundingClientRect().height + gap;
      };
      const updateGalleryArrows = () => {
        const atStart = galleryScroll.scrollTop <= 1;
        const atEnd = galleryScroll.scrollTop + galleryScroll.clientHeight >= galleryScroll.scrollHeight - 1;
        if (galleryPrevious instanceof HTMLButtonElement) galleryPrevious.disabled = atStart;
        if (galleryNext instanceof HTMLButtonElement) galleryNext.disabled = atEnd;
      };
      const scrollGallery = (direction) => {
        if (!desktopGallery.matches) return;
        galleryScroll.scrollBy({ top: direction * galleryStep(), behavior: 'smooth' });
      };
      const revealThumbnail = (button) => {
        if (!desktopGallery.matches || !(button instanceof HTMLElement)) return;
        const buttonTop = button.offsetTop;
        const buttonBottom = buttonTop + button.offsetHeight;
        const viewportTop = galleryScroll.scrollTop;
        const viewportBottom = viewportTop + galleryScroll.clientHeight;
        if (buttonTop < viewportTop) galleryScroll.scrollTo({ top: buttonTop, behavior: 'smooth' });
        if (buttonBottom > viewportBottom) galleryScroll.scrollTo({ top: buttonBottom - galleryScroll.clientHeight, behavior: 'smooth' });
      };

      galleryPrevious?.addEventListener('click', () => scrollGallery(-1), { signal });
      galleryNext?.addEventListener('click', () => scrollGallery(1), { signal });
      galleryScroll.addEventListener('scroll', updateGalleryArrows, { signal, passive: true });
      desktopGallery.addEventListener('change', updateGalleryArrows, { signal });
      this.querySelectorAll('[data-winks-media-button]').forEach((button) => {
        button.addEventListener('click', () => revealThumbnail(button), { signal });
      });
      requestAnimationFrame(updateGalleryArrows);
    }

    const zoomButtons = this.querySelectorAll('[data-winks-zoom-button]');
    const lightbox = this.querySelector('[data-winks-lightbox]');
    const lightboxImage = this.querySelector('[data-winks-lightbox-image]');
    const lightboxClose = this.querySelector('[data-winks-lightbox-close]');
    if (zoomButtons.length && lightbox instanceof HTMLDialogElement && lightboxImage instanceof HTMLImageElement) {
      zoomButtons.forEach((zoomButton) => zoomButton.addEventListener('click', () => {
        const activePanel = zoomButton.closest('[data-winks-media-panel]');
        const activeImage = activePanel?.querySelector('img');
        if (!(activePanel instanceof HTMLElement) || !(activeImage instanceof HTMLImageElement)) return;

        lightboxImage.src = activePanel.dataset.winksZoomSource || activeImage.currentSrc || activeImage.src;
        lightboxImage.alt = activeImage.alt || 'Enlarged product image';
        lightbox.showModal();
      }, { signal }));
      lightboxClose?.addEventListener('click', () => lightbox.close(), { signal });
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) lightbox.close();
      }, { signal });
    }

    // Without JavaScript, planned routines stay disabled and the native form
    // remains a one-time purchase. Once the component is mounted, the cards
    // become inspectable while submit remains blocked until Shopify provides
    // a real selling-plan id.
    this.querySelectorAll('[data-winks-segmented-plan]').forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.value === 'once') return;
      const card = input.closest('[data-winks-segmented-card]');
      const hasExactSellingPlan = (input.dataset.sellingPlanId || '').trim() !== '';
      const isDisplayOnly = card?.dataset.winksSegmentedPreviewFallback === 'true';
      const variantAvailable = card?.dataset.available === 'true';
      input.disabled = !variantAvailable || (!hasExactSellingPlan && !isDisplayOnly);
    });

    this.querySelectorAll('[data-winks-segmented-plan]').forEach((input) => {
      input.addEventListener('change', () => {
        this.#render({ animateDetails: true, anchor: input.closest('[data-winks-segmented-card]') });
      }, { signal });
    });

    this.querySelectorAll('[data-winks-segmented-quantity]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const quantityAppliesToPurchase = this.dataset.quantityScope === 'purchase';
          if (!quantityAppliesToPurchase) {
            const shortPlan = this.querySelector('[data-winks-segmented-plan="short"]');
            if (!(shortPlan instanceof HTMLInputElement) || shortPlan.disabled) return;
            shortPlan.checked = true;
          }
          this.dataset.quantity = button.dataset.winksSegmentedQuantity || '1';
          this.#render();
        },
        { signal },
      );
    });

    // The whole card is the control (owner direction 2026-07-20): clicking
    // anywhere on a plan card selects its plan, not just the label row.
    // Quantity tiles keep their own handler, so they are excluded here.
    // One-time purchase is a peer card since the 2026-08-22 offer change:
    // selecting it no longer submits on tap; the shared CTA adds to cart.
    this.querySelectorAll('[data-winks-segmented-card]').forEach((card) => {
      card.addEventListener(
        'click',
        (event) => {
          if (event.target instanceof Element && event.target.closest('button, a')) return;
          const input = card.querySelector('[data-winks-segmented-plan]');
          if (!(input instanceof HTMLInputElement) || input.disabled) return;
          if (input.checked) return;

          input.checked = true;

          // A radio's native `change` event can fire after its bubbling
          // `click` event. Render synchronously so the hidden selling-plan
          // field never lags behind the visible selection.
          this.#render({ animateDetails: true, anchor: card });
        },
        { signal },
      );
    });

    this.#selectInitialPlan();
    this.#render();

    // Rules arrive after first paint, so the card opens on its Liquid-rendered
    // ladder and reprices once. The signal check matters in the theme editor:
    // a section re-render can tear this instance down while the request is
    // still in flight, and rendering into a detached component would throw.
    // Older article/EPDP sections mount this shared component without the
    // variant metadata needed to match a Skio rule. Leave those established
    // offers untouched instead of fetching rules they can never apply.
    const hasSkioTarget = this.querySelector('[data-winks-segmented-card][data-variant-id]');
    if (this.dataset.skioRules !== 'false' && hasSkioTarget) {
      this.dataset.skioState = 'loading';
      fetchSkioRules().then((rules) => {
        if (signal.aborted) return;
        const variantIds = new Set(
          [...this.querySelectorAll('[data-winks-segmented-card][data-variant-id]')]
            .map((card) => card.dataset.variantId?.trim())
            .filter(Boolean),
        );
        const hasMatchingRule = rules.some((rule) => [...rule.variantIds].some((id) => variantIds.has(id)));
        if (!hasMatchingRule) {
          this.dataset.skioState = 'fallback';
          return;
        }
        this.#skioRules = rules;
        this.dataset.skioState = 'live';
        this.#render();
      });
    } else {
      this.dataset.skioState = 'disabled';
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #selectInitialPlan() {
    // The 28-day routine owns the initial interactive state whenever it is
    // available. Liquid keeps One Time Purchase checked as the no-JavaScript
    // safety fallback, so the runtime must deliberately promote the routine
    // after it has verified the selling plan or preview state. Without this,
    // the card looked expanded from Liquid while its radio and quantity
    // controls were actually deselected.
    const preferredRoutine = this.querySelector('[data-winks-segmented-plan="short"]:not(:disabled)');
    if (preferredRoutine instanceof HTMLInputElement) {
      preferredRoutine.checked = true;
      return;
    }

    const checked = this.querySelector('[data-winks-segmented-plan]:checked:not(:disabled)');
    if (checked) return;

    const firstAvailable = this.querySelector('[data-winks-segmented-plan]:not(:disabled)');
    if (firstAvailable instanceof HTMLInputElement) firstAvailable.checked = true;
  }

  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    const wholeDollars = cents % 100 === 0;

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      // narrowSymbol keeps a CAD store on "$49" instead of "CA$49". The default
      // symbol form disagreed with Liquid's money filter, so every price grew a
      // CA$ prefix the moment this script ran — and the wider string reflowed
      // the card, which is what made the section jump on quantity changes.
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: wholeDollars ? 0 : 2,
      maximumFractionDigits: wholeDollars ? 0 : 2,
    }).format(cents / 100);
  }

  get #shortCard() {
    return this.querySelector('[data-winks-segmented-plan="short"]')?.closest('[data-winks-segmented-card]');
  }

  get #longCard() {
    return this.querySelector('[data-winks-segmented-plan="long"]')?.closest('[data-winks-segmented-card]');
  }

  // Return the Skio-discounted line total, or null when no live rule covers
  // this card and quantity. The base must be undiscounted to avoid applying a
  // volume tier twice.
  #skioLineTotal(card, baseUnitCents, quantity) {
    if (!this.#skioRules.length || !(baseUnitCents > 0) || !(quantity > 0)) return null;

    const rule = resolveSkioRule(this.#skioRules, card?.dataset.variantId, quantity);
    if (!rule) return null;

    const lineTotal = baseUnitCents * quantity;
    const discount = skioDiscountCents(rule, lineTotal);
    return discount === null ? null : lineTotal - discount;
  }

  // The 12-week routine submits a fixed multi-unit line rather than exposing
  // the household quantity control. It still qualifies for the matching Skio
  // volume tier when it uses the same variant.
  #bundleTotal(card, { renewal = false } = {}) {
    const fallback = renewal
      ? Number(card?.dataset.renewalPrice || card?.dataset.price || '0')
      : Number(card?.dataset.price || '0');

    const units = Number(card?.dataset.units || '1');
    const unitPrice = Number(renewal ? card?.dataset.renewalUnitPrice : card?.dataset.unitPrice);
    if (!Number.isFinite(units) || units < 1 || !(unitPrice > 0)) return fallback;

    const skioTotal = this.#skioLineTotal(card, unitPrice, units);
    return skioTotal !== null ? skioTotal : fallback;
  }

  // Volume tiers (owner direction 2026-07-20): the 28-night card carries a
  // per-bag price for each sleeper count in data-qty-prices, so raising the
  // quantity lowers the per-bag price instead of multiplying a flat one.
  // Falls back to the flat card price when a tier is absent or malformed.
  #shortPerBag(quantity) {
    const shortCard = this.#shortCard;
    const base = Number(shortCard?.dataset.price || '0');
    const skioTotal = this.#skioLineTotal(shortCard, base, quantity);
    if (skioTotal !== null) return Math.round(skioTotal / quantity);

    const tiers = (shortCard?.dataset.qtyPrices || '').split(',').map(Number);
    const tier = tiers[quantity - 1];
    return Number.isFinite(tier) && tier > 0 ? tier : base;
  }

  // Renewal tiers are separate from first-shipment tiers. This keeps every
  // displayed future charge honest when household quantity changes instead of
  // multiplying the single-sleeper $67 renewal across every tier.
  #shortRenewalPerBag(quantity) {
    const shortCard = this.#shortCard;
    const base = Number(shortCard?.dataset.renewalPrice || shortCard?.dataset.price || '0');
    const skioTotal = this.#skioLineTotal(shortCard, base, quantity);
    if (skioTotal !== null) return Math.round(skioTotal / quantity);

    const tiers = (shortCard?.dataset.qtyRenewalPrices || '').split(',').map(Number);
    const tier = tiers[quantity - 1];
    return Number.isFinite(tier) && tier > 0 ? tier : base;
  }

  #shortLineTotal(quantity) {
    const shortCard = this.#shortCard;
    const base = Number(shortCard?.dataset.price || '0');
    const skioTotal = this.#skioLineTotal(shortCard, base, quantity);
    return skioTotal !== null ? skioTotal : this.#shortPerBag(quantity) * quantity;
  }

  // Shopify applies the approved automatic discount to one-time purchases at
  // two bags. Liquid mirrors that live rule as a per-bag tier so the buy box
  // shows the same unit price the cart will charge; the base remains the live
  // Shopify variant price.
  #oncePerBag(quantity) {
    const onceCard = this.querySelector('[data-winks-segmented-plan="once"]')?.closest('[data-winks-segmented-card]');
    const base = Number(onceCard?.dataset.price || '0');
    const tiers = (onceCard?.dataset.qtyPrices || '').split(',').map(Number);
    const tier = tiers[quantity - 1];
    return Number.isFinite(tier) && tier > 0 ? tier : base;
  }

  #setShortDetailsOpen(open, animateDetails) {
    const content = this.querySelector('[data-winks-segmented-detail-content="short"]');
    if (!(content instanceof HTMLElement)) return;

    const quantityDetails = content.querySelector('[data-winks-segmented-details]');
    const currentlyOpen = !content.hidden;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldAnimate = animateDetails && !reduceMotion && currentlyOpen !== open;

    content.getAnimations().forEach((animation) => animation.cancel());

    if (!shouldAnimate) {
      content.hidden = !open;
      if (quantityDetails instanceof HTMLElement) quantityDetails.hidden = !open;
      return;
    }

    if (open) {
      content.hidden = false;
      if (quantityDetails instanceof HTMLElement) quantityDetails.hidden = false;
    }

    const startHeight = open ? 0 : content.scrollHeight;
    const endHeight = open ? content.scrollHeight : 0;
    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: open ? 0 : 1 },
        { height: `${endHeight}px`, opacity: open ? 1 : 0 },
      ],
      { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );

    animation.addEventListener('finish', () => {
      content.hidden = !open;
      if (quantityDetails instanceof HTMLElement) quantityDetails.hidden = !open;
    }, { once: true });
  }

  #stabilizeViewportAnchor(anchor, startTop) {
    if (!(anchor instanceof HTMLElement) || startTop === null) return;

    const deadline = performance.now() + 340;
    const correctPosition = () => {
      if (!anchor.isConnected) return;
      const delta = anchor.getBoundingClientRect().top - startTop;
      if (Math.abs(delta) > 0.5) {
        // The storefront enables smooth scrolling globally. Letting that rule
        // animate every per-frame correction queues competing scrolls and
        // makes the anchor lag behind the collapsing card at 320px. Override
        // it only for this synchronous correction; the card's own motion stays
        // smooth and the page-level preference is restored immediately.
        const previousScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollBy(0, delta);
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      }
      if (performance.now() < deadline) requestAnimationFrame(correctPosition);
    };
    requestAnimationFrame(correctPosition);
  }

  #render({ animateDetails = false, anchor = null } = {}) {
    const selected = this.querySelector('[data-winks-segmented-plan]:checked:not(:disabled)');
    if (!(selected instanceof HTMLInputElement)) return;

    const anchorRect = anchor instanceof HTMLElement ? anchor.getBoundingClientRect() : null;
    const anchorIsVisible = anchorRect !== null && anchorRect.bottom > 0 && anchorRect.top < window.innerHeight;
    const anchorTop = animateDetails
      && anchorIsVisible
      ? anchorRect.top
      : null;

    const selectedKey = selected.dataset.winksSegmentedPlan || 'once';
    const selectedCard = selected.closest('[data-winks-segmented-card]');
    const isDisplayOnly = selectedCard?.dataset.winksSegmentedPreviewFallback === 'true';
    const shortQuantity = Number(this.dataset.quantity || '1');
    const shortFirstShipmentTotal = this.#shortLineTotal(shortQuantity);
    const shortRenewalUnitPrice = this.#shortRenewalPerBag(shortQuantity);
    const quantityAppliesToPurchase = this.dataset.quantityScope === 'purchase';
    // The card declares how many units its displayed price covers (data-units).
    // Reading the submitted quantity from that same attribute is what keeps the
    // price on screen and the price at checkout from ever disagreeing: a card
    // priced as a total for N units always adds exactly N units.
    const cardUnits = Number(selectedCard?.dataset.units || '1');
    const quantity = selectedKey === 'short' || (quantityAppliesToPurchase && selectedKey === 'once')
      ? shortQuantity
      : Number.isFinite(cardUnits) && cardUnits > 0 ? cardUnits : 1;
    this.querySelectorAll('[data-winks-segmented-card]').forEach((card) => {
      const input = card.querySelector('[data-winks-segmented-plan]');
      const isSelected = input === selected;
      card.classList.toggle('is-selected', isSelected);
      const cardKey = input?.dataset.winksSegmentedPlan;
      const usesAnchoredCollapse = this.dataset.winksSegmentedLayout === 'anchored-collapse';
      const usesPersistentDetails = this.dataset.winksSegmentedLayout === 'persistent-details';
      card.classList.toggle('is-collapsed', usesAnchoredCollapse && cardKey === 'short' && !isSelected);
      const details = card.querySelector('[data-winks-segmented-details]');
      const detailContent = card.querySelector('[data-winks-segmented-detail-content="short"]');
      if (usesPersistentDetails && cardKey === 'short') {
        if (detailContent instanceof HTMLElement) detailContent.hidden = false;
        if (details instanceof HTMLElement) details.hidden = false;
      } else if (details instanceof HTMLElement && !(usesAnchoredCollapse && cardKey === 'short')) {
        details.hidden = !isSelected || (input instanceof HTMLInputElement && input.disabled);
      }
      if (details instanceof HTMLElement && input instanceof HTMLInputElement) {
        input.setAttribute('aria-expanded', details.hidden ? 'false' : 'true');
      }
    });

    if (this.dataset.winksSegmentedLayout === 'anchored-collapse') {
      const shortPlan = this.querySelector('[data-winks-segmented-plan="short"]');
      this.#setShortDetailsOpen(
        selectedKey === 'short' && shortPlan instanceof HTMLInputElement && !shortPlan.disabled,
        animateDetails,
      );
      this.#stabilizeViewportAnchor(anchor, anchorTop);
    }

    this.querySelectorAll('[data-winks-segmented-quantity]').forEach((button) => {
      const isSelected = Number(button.dataset.winksSegmentedQuantity) === shortQuantity;
      button.classList.toggle('active', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const optionQuantity = Number(button.dataset.winksSegmentedQuantity || '1');
      const perBagPrice = selectedKey === 'once'
        ? this.#oncePerBag(optionQuantity)
        : this.#shortPerBag(optionQuantity);
      const displayPrice = Math.round(perBagPrice / 28);
      const unitPriceNode = button.querySelector('[data-winks-segmented-quantity-unit-price]');
      if (unitPriceNode instanceof HTMLElement) unitPriceNode.textContent = this.#money(displayPrice);
      const priceBasisNode = button.querySelector('[data-winks-segmented-quantity-price-basis]');
      if (priceBasisNode instanceof HTMLElement) priceBasisNode.textContent = '/night';
    });

    this.querySelectorAll('[data-winks-segmented-dynamic-price]').forEach((node) => {
      const card = node.closest('[data-winks-segmented-card]');
      const cardKey = card?.querySelector('[data-winks-segmented-plan]')?.dataset.winksSegmentedPlan;
      const cents = cardKey === 'short'
        ? shortQuantity > 1 ? this.#shortPerBag(shortQuantity) : shortFirstShipmentTotal
        : cardKey === 'once' && shortQuantity > 1
          ? this.#oncePerBag(shortQuantity)
          : this.#bundleTotal(card);
      node.textContent = this.#money(cardKey === 'long' ? ceilToWholeDollars(cents) : cents);
    });

    this.querySelectorAll('[data-winks-segmented-dynamic-compare-at]').forEach((node) => {
      const card = node.closest('[data-winks-segmented-card]');
      // The subscription card switches to a per-bag headline at quantity two,
      // so its comparison remains on that same per-bag basis.
      const cents = Number(card?.dataset.compareAt || '0');
      node.textContent = this.#money(cents);
    });

    const perBagSuffix = this.querySelector('[data-winks-segmented-per-bag-suffix]');
    if (perBagSuffix instanceof HTMLElement) perBagSuffix.hidden = shortQuantity === 1;

    const oncePerBagSuffix = this.querySelector('[data-winks-segmented-once-per-bag-suffix]');
    if (oncePerBagSuffix instanceof HTMLElement) oncePerBagSuffix.hidden = shortQuantity === 1;

    const inlineCompare = this.querySelector('[data-winks-segmented-inline-compare]');
    if (inlineCompare instanceof HTMLElement) inlineCompare.hidden = false;

    const shortBadge = this.querySelector('[data-winks-segmented-short-badge]');
    if (shortBadge instanceof HTMLElement) {
      const shortCard = shortBadge.closest('[data-winks-segmented-card]');
      const compareAt = Number(shortCard?.dataset.compareAt || '0') * shortQuantity;
      const total = this.#shortLineTotal(shortQuantity);
      const savings = compareAt > total ? Math.round(((compareAt - total) * 100) / compareAt) : 0;
      const label = shortBadge.dataset.badgeLabel?.trim() || '';
      shortBadge.textContent = [label, savings > 0 ? `${savings}% off` : ''].filter(Boolean).join(' · ');
    }

    const longCard = this.#longCard;
    if (longCard instanceof HTMLElement) {
      const longTotal = this.#bundleTotal(longCard);

      const longPerNight = longCard.querySelector('[data-winks-segmented-long-pernight]');
      const nights = Number(longCard.dataset.nights || '0');
      if (longPerNight instanceof HTMLElement && nights > 0) {
        longPerNight.textContent = this.#money(Math.round(longTotal / nights));
      }

      const longRenewal = longCard.querySelector('[data-winks-segmented-long-renewal]');
      if (longRenewal instanceof HTMLElement) {
        longRenewal.textContent = this.#money(ceilToWholeDollars(this.#bundleTotal(longCard, { renewal: true })));
      }

      const longBadge = longCard.querySelector('[data-winks-segmented-long-badge]');
      if (longBadge instanceof HTMLElement) {
        const compareAt = Number(longCard.dataset.compareAt || '0');
        const savings = compareAt > longTotal ? Math.round(((compareAt - longTotal) * 100) / compareAt) : 0;
        const label = longBadge.dataset.badgeLabel?.trim() || '';
        longBadge.textContent = [label, savings > 0 ? `${savings}% off` : ''].filter(Boolean).join(' · ');
      }
    }

    // The selector communicates quantity and per-night economics, while each
    // purchase card carries the selected per-bag price. Hide the static "1 bag"
    // fallback once the live selector is available so the hierarchy stays clear.
    const shortDetail = this.querySelector('[data-winks-segmented-short-detail]');
    const hasHouseholdSelector = this.querySelector('[data-winks-segmented-quantity]') instanceof HTMLElement;
    if (shortDetail instanceof HTMLElement && hasHouseholdSelector) shortDetail.hidden = true;

    // Per-night follows the volume tier: cheaper bags mean a cheaper night,
    // so the value line and the tier stay in agreement at every quantity.
    const perNightValue = this.querySelector('[data-winks-segmented-pernight-value]');
    if (perNightValue instanceof HTMLElement) {
      const selectedPerBag = selectedKey === 'once'
        ? this.#oncePerBag(shortQuantity)
        : this.#shortPerBag(shortQuantity);
      perNightValue.textContent = this.#money(Math.round(selectedPerBag / 28));
    }

    // Keep the compact value line on a per-bag basis at every quantity. The
    // selected card carries the ongoing price, cadence, and cancellation term.
    const inlineRenewal = this.querySelector('[data-winks-segmented-inline-renewal]');
    if (inlineRenewal instanceof HTMLElement) inlineRenewal.textContent = this.#money(shortRenewalUnitPrice);

    const quantityInput = this.querySelector('[data-winks-segmented-form-quantity]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(quantity);

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      const variantId = selectedCard?.dataset.variantId || '';
      if (variantId) variantInput.value = variantId;
    }

    const sellingPlanInput = this.querySelector('[data-winks-segmented-form-plan]');
    if (sellingPlanInput instanceof HTMLInputElement) {
      const sellingPlanId = selected.dataset.sellingPlanId || '';
      sellingPlanInput.value = sellingPlanId;
      sellingPlanInput.disabled = selectedKey === 'once' || sellingPlanId === '';
    }

    const routineProperty = this.querySelector('[data-winks-segmented-routine-property]');
    if (routineProperty instanceof HTMLInputElement) {
      const title = selectedCard?.querySelector('.winks-plan__title')?.textContent?.trim() || selected.value;
      routineProperty.value = selectedKey === 'once' ? 'One-time purchase' : title;
    }

    const ctaText = this.querySelector('.add-to-cart-text__content > span > span');
    if (ctaText) ctaText.textContent = 'Add to Cart';

    const ctaButton = this.querySelector('[ref="addToCartButton"]');
    if (ctaButton instanceof HTMLButtonElement) {
      // Availability belongs to the selected variant. The 12-week option can
      // point at a dedicated bundle variant whose inventory differs from the
      // base bag, so the root/base availability cannot safely gate every card.
      const selectedVariantAvailable = selectedCard?.dataset.available === 'true';
      // Password-protected routine previews use the same form path as the
      // earlier buy box. When no selling-plan ID exists, Shopify receives the
      // selected variant and quantity without a selling_plan field. Keep those
      // display-only cards inspectable, but never let subscription copy submit
      // a one-time line.
      const selectedPlanId = (selected.dataset.sellingPlanId || '').trim();
      // A routine that advertises free gifts fails closed until all three
      // Shopify gift products resolve; the card only carries the readiness
      // flag while it is showing gifts, so a gift-free routine is ungated.
      const giftReadiness = selectedCard?.dataset.winksGiftProductsReady;
      const giftsReady = giftReadiness === undefined || giftReadiness === 'true';
      const selectedRoutineConnected = selectedKey === 'once'
        || (!isDisplayOnly && selectedPlanId !== '' && giftsReady);
      ctaButton.disabled = !selectedVariantAvailable || !selectedRoutineConnected;
      ctaButton.setAttribute('aria-disabled', ctaButton.disabled ? 'true' : 'false');
    }

  }
}

if (!customElements.get('winks-segmented-buybox')) {
  customElements.define('winks-segmented-buybox', WinksSegmentedBuybox);
}
